import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';

import BackToJobListButton from '../../../components/BackToJobListButton/BackToJobListButton';
import PageBanner from '../../../components/Banner/PageBanner';
import PaginationControl from '../../../components/PaginationControl/PaginationControl';
import BookmarkJobInfo from './BookmarkJobInfo/BookmarkJobInfo';
import BookmarkLists from './BookmarkList/BookmarkLists';
import useGetBookmarkedLocumJobs from '../../../hooks/dashboard/jobs/useGetBookmarkedLocumJobs';
import { useAppSelector } from '../../../redux/store';
import { useNavigate } from 'react-router';
import LeftTriangleIcon from '../../../components/Icons/LeftTriangleIcon';
import { DASHBOARD } from '../../../routes/routeConstants';

const Bookmarks = () => {
  const [showMobileInfo, setShowMobileInfo] = useState<boolean>(false);

  const navigate = useNavigate();

  // Local pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const perPage = 10;

  const THRESHOLD = 884;

  const [isWide, setIsWide] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth > THRESHOLD : false
  );

  // Get the data fetching hook and Redux loading state
  const { getBookmarkedLocumJobs, loading } = useGetBookmarkedLocumJobs();
  // Read totalPages from Redux (set by the useGetBookmarkedLocumJobs hook)
  const { totalPages } = useAppSelector((state) => state.bookmarkedLocumJobs);

  // Fetch bookmarked jobs whenever currentPage or perPage changes
  useEffect(() => {
    getBookmarkedLocumJobs({
      page: currentPage.toString(),
      perPage: perPage.toString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage]);

  // Handler for PaginationControl
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  useEffect(() => {
    const handleResize = () => setIsWide(window.innerWidth > THRESHOLD);

    // run once on mount
    handleResize();

    // listen for resize
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Container>
      <PageBanner
        label="Bookmarks"
        description="Welcome to your bookmark dashboard"
      />
      {showMobileInfo && !isWide ? (
        <BackToJobListButton
          showMobileInfo={showMobileInfo}
          setShowMobileInfo={setShowMobileInfo}
        />
      ) : (
        <NavigationArea onClick={() => navigate(`/${DASHBOARD}`)}>
          <LeftTriangleIcon />
          Back to Dashboard
        </NavigationArea>
      )}

      <BookmarkWrapper>
        <JobViewArea>
          <BookmarkLists
            showMobileInfo={showMobileInfo}
            setShowMobileInfo={setShowMobileInfo}
            loading={loading}
            reset={currentPage}
          />
          <BookmarkJobInfo
            showMobileInfo={showMobileInfo}
            reset={currentPage}
          />
        </JobViewArea>

        <PaginationShell>
          <PaginationControl
            endPage={totalPages > 0 ? totalPages : 1}
            changePage={handlePageChange}
            defaultNumOfPagination={5}
          />
        </PaginationShell>
      </BookmarkWrapper>
    </Container>
  );
};

export default Bookmarks;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 884px) {
    gap: 8px;
  }
`;

const JobViewArea = styled.div`
  display: grid;
  grid-template-columns: 316px 1fr;
  gap: 32px;
  height: 80vh;
  overflow: auto;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }

  @media (max-width: 1024px) {
    grid-template-columns: 300px 1fr;
  }

  @media (max-width: 884px) {
    grid-template-columns: 1fr;
    gap: 16px;
    align-content: start;
  }
`;

const BookmarkWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  background-color: white;
  padding: 2rem;
  border-radius: 0.75rem;
  margin-bottom: 54px;
  background-color: ${({ theme }) => theme.palette.backgroundColor};

  @media (max-width: 1024px) {
    padding: 0;
    margin-left: -30px;
    margin-right: -30px;
  }

  @media (max-width: 884px) {
    margin-left: 0;
    margin-right: 0;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    margin-left: 0;
    margin-right: 0;
  }
`;

const PaginationShell = styled.div`
  display: grid;
  place-content: center;
  grid-column: span 2;

  @media (max-width: 884px) {
  }
`;

const NavigationArea = styled.div`
  display: flex;
  align-items: center;
  font-size: 18px;
  color: #2f80ed;
  font-weight: 500;
  cursor: pointer;
  width: fit-content;

  & > svg {
    transition: transform 0.3s ease-in-out;
    transform: translateX(0);
  }

  &:hover > svg {
    transform: translateX(-8px);
  }
`;
